import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  MenuItem,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Chip,
  Select,
} from "@mui/material";
import useFetchResources from "../../hooks/useFetchResources";
import { useToast } from "../../services/utils";
import { createRule } from "../../services/rulesService";
import { CheckCircleRounded } from "@mui/icons-material";
import {
  getResourceColumnsDetail,
  validateConfigRequest,
} from "../../services/resourcesService";
import AutoCompleteDomainList from "../../components/Molecules/Domain/AutoCompleteDomainList";
import RulesDataTable from "../../components/DataGrids/RulesDataGrid";
import ResourceFilter from "../../components/Rules/ResourceFilter";
import CustomComparisionTab from "../../components/Rules/CustomComparisionTab";
import { useNavigate } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { GridColDef } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import ToleranceDialog from "../../components/Dialogs/ToleranceDialog";
import {
  addEditRuleSchema,
  comparisonScoringConfigFormSchema,
} from "../../schemas";
import ConfirmationDialog from "../../components/Dialogs/ConfirmationDialog";
import { Popper } from "@mui/base";
import Loader from "../../components/Molecules/Loader/Loader";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useRuleContext } from "../../contexts/RuleContext";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import { useAuthTagContext } from "../../contexts/TagAndSystemProvider";
import useFetchActiveAuthTagsList from "../../hooks/useFetchActiveAuthTagsList";
import AdhocQueries from "./AdhocQueries";
import TagsDialog from "../../components/Dialogs/TagsDialog";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import AddCircleIcon from "@mui/icons-material/AddCircleOutline";
import CreateIcon from "@mui/icons-material/CreateOutlined";
import {
  comparisonScoringConfigDefaults,
  mergeType,
} from "../../services/constants";
import { combineArrays } from "../../services/utils";
import SecondaryMergeResource from "../../components/Molecules/Rule/SecondaryMergeResource";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import { IconAddRowBase, IconRemoveBase } from "../../common/utils/icons";

import { EditAuthTags } from "../../components/AuthTags";
import SelectedResourceList from "../../components/Rules/SelectedResourceList";
import ComparisonScoringConfigForm from "../../components/Scoring/ComparisonScoringConfigForm";
import useFetchComparisonScoringConfig from "../../hooks/useFetchComparisonScoringConfig";
import { createComparisonScoringConfig } from "../../services/scoringService";
import { useAuth } from "../../contexts/AuthContext";
import ViewPayloadDialog from "../../components/Dialogs/ViewPayloadDialog";
import ViewJsonSplitButton from "../../components/common/ViewJsonSplitButton";
import {
  isComparisonCategoryWeightTotalValid,
  isSchemaValidSync,
  resolveScoringConfigValues,
} from "../../utils/scoringConfigFormValidation";
import {
  MISSING_REQUIRED_COLUMNS_MESSAGE,
  getRulesWithMissingColumns,
} from "../../utils/comparisonRuleUtils";

const AddRule: React.FC = () => {
  //ruleContext imports
  const {
    customFilters,
    setCustomFilters,
    allVariablesList,
    setAllVariablesList,
    multipleResourcesData,
    setMultipleResourcesData,
    customComparisonRules,
    setCustomComparisonRules,
    toleranceData,
    setToleranceData,
    selectedResourceIds,
    setSelectedResourceIds,
    selectedResourceColumns,
    setSelectedResourceColumns,
  } = useRuleContext();

  const { globalVariables, setGlobalVariables, availColumns } =
    useRuleResourceContext();

  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const [scoringConfigValues, setScoringConfigValues] = useState<any>(null);
  const [isScoringConfigValid, setIsScoringConfigValid] = useState(true);
  // The scoring form is lazy-mounted on a non-default tab. Until it emits
  // values, validate the same canonical defaults the form initializes.
  const effectiveScoringConfigValues = resolveScoringConfigValues(
    scoringConfigValues,
    comparisonScoringConfigDefaults,
  );
  const canSaveScoringConfig =
    isScoringConfigValid &&
    isSchemaValidSync(
      comparisonScoringConfigFormSchema,
      effectiveScoringConfigValues,
    ) &&
    isComparisonCategoryWeightTotalValid(effectiveScoringConfigValues);
  //states
  const [activeTab, setActiveTab] = useState<string>("domainComparisonRule");
  const [currentDomainId, setCurrentDomainId] = useState<
    string | number | null
  >(0);
  const [resultData, setResultData] = useState<any>({
    name: "",
    description: "",
    domain_id: null,
    domain_name: "",
    rule_tags: [],
    rule_schema: {},
  });
  const [selectedMergeType, setSelectedMergeType] = useState<any>([
    {
      rId: "",
      merge_type: undefined,
    },
  ]);
  const [resIds, setResIds] = useState<any>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [domainComparisonRules, setDomainComparisonRules] = useState<any[]>([]);
  const [resourceIds, setResourceIds] = useState<any[]>([]);
  const [finalRule, setFinalRule] = useState<any>();
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isSelecting, setIsSelecting] = useState<boolean>(false);
  const [selectedDomainData, setSelectedDomainData] = useState<any>(null);
  const [expandedRow, setExpandedRow] = useState<any>([]);
  const [selectedRowId, setSelectedRowId] = useState<any>(null);
  const [openToleranceDialog, setOpenToleranceDialog] =
    useState<boolean>(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [domainRuleArr, setDomainRuleArr] = useState<any>([]);
  const [openResourceConfirmation, setOpenResourceConfirmation] =
    useState(false);
  const [resourceDataObj, setResourceDataObj] = useState<any>([]);
  const [adhocQueryData, setAdhocQueryData] = useState<any>([]);
  const [showTagsDialog, setShowTagsDialog] = useState<any>({});
  const [selectedTags, setSelectedTags] = useState<any>({
    key: "",
    values: [],
  });
  const [tagColResult, setTagColResult] = useState<any>([
    {
      key: "",
      values: [],
    },
  ]);
  const [possibleMergeColumns, setPossibleMergeColumns] = useState<any>([]);
  const [tagId, setTagId] = useState("");
  const [sampleAdhocQuery, setSampleAdhocQuery] = useState<any>("");
  const [selectResourceValue, setSelectResourceValue] = useState<any>([]);
  const [secondaryMergeResource, setSecondaryMergeResource] = useState<any>([]);
  const [isPayloadDialogOpen, setIsPayloadDialogOpen] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResponse, setValidationResponse] = useState<any>(null);

  //hooks
  const [resourcesData] = useFetchResources({ currentDomainId, setIsLoading });

  // Get the setter function from context (following linkedServicesData pattern)
  const { setAllAuthTagsData } = useAuthTagContext();

  // Fetch auth tags data directly in this component (following linkedServicesData pattern)
  const [authTagsData] = useFetchActiveAuthTagsList({
    setIsLoading,
  });

  // Update context state when auth tags data is fetched (following linkedServicesData pattern)
  useEffect(() => {
    if (authTagsData) {
      setAllAuthTagsData(authTagsData);
    }
  }, [authTagsData, setAllAuthTagsData]);

  const cdmData: string[] = [];

  /**
   * Set current domain id & Update result data state
   * @param data
   */
  const handelChangeDomain = (dataObj: any, data: any) => {
    if (!data?.id) {
      setOpenConfirmation(true);
      return;
    }
    setSelectedDomainData(data);
    setCurrentDomainId(data?.id);
    setResultData({
      ...resultData,
      domain_id: data?.id,
      domain_code: data?.domain_code,
      domain_name: data?.domain_name,
    });
    validateField("domain_id", data?.id);
  };

  useEffect(() => {
    const resData: any[] = [];

    // Check if resourceIds and resourcesData are available
    if (resourceIds && resourceIds.length > 0 && resourcesData) {
      resourceIds.forEach((resId: any) => {
        const resourceData = resourcesData.find(
          (resource: any) => resource.id === resId,
        );
        if (resourceData) {
          resData.push(resourceData);
        }
      });
    }

    // Construct the query string
    const query = `SELECT * FROM <TABLE_NAME> WHERE "${
      availColumns && availColumns.length > 0 ? availColumns[0] : ""
    }" <> "${
      availColumns && availColumns.length > 1
        ? availColumns[1]
        : availColumns && availColumns.length > 0
          ? availColumns[0]
          : ""
    }"`;

    setSampleAdhocQuery(query);
  }, [resourceIds, resourcesData, availColumns]);

  useEffect(() => {
    if (resourcesData) {
      setMultipleResourcesData(resourcesData);
    }
  }, [resourcesData]);

  useEffect(() => {
    if (
      selectedResourceIds &&
      selectedResourceIds.length > 0 &&
      selectedResourceColumns &&
      selectedResourceColumns.length > 0
    ) {
      const matchingIds = selectedResourceIds
        .filter((resource: any) => {
          const selectedResource = selectedResourceColumns.find(
            (selected: any) => selected.rId === resource.rId,
          );
          if (
            selectedResource &&
            resource?.resource_column_properties?.resource_columns
          ) {
            return (
              selectedResource.columns.length ===
              resource.resource_column_properties.resource_columns.length
            );
          }
          return false;
        })
        .map((resource: any) => resource.rId);

      if (
        matchingIds &&
        matchingIds.length > 0 &&
        customComparisonRules &&
        customComparisonRules.length > 0
      ) {
        const updatedComparisonRules = customComparisonRules.filter(
          (rule: any) => {
            // Check if rule.resource exists before accessing its properties
            if (
              rule.resource &&
              rule.resource.left_operand &&
              rule.resource.right_operand
            ) {
              // Check if updatedColumnsData contains any empty columns and if so, remove matching rules
              return matchingIds.every((id: any) => {
                // Filter out the rules where resource_id matches the id in updatedColumnsData
                return (
                  rule.resource.left_operand.resource_id !== id &&
                  rule.resource.right_operand.resource_id !== id
                );
              });
            } else {
              // If rule.resource is undefined or doesn't have required properties, keep the rule
              return true;
            }
          },
        );
        // toast.warning("Since all columns are unique, the custom comparison for this resource has been removed.");
        setCustomComparisonRules(updatedComparisonRules);
      }
    }
  }, [selectedResourceColumns, selectedResourceIds]);
  /**
   * On change rule name update result data with new name
   * @param e
   */
  const handleChangeRule = (e: any) => {
    setResultData({ ...resultData, [e.target.name]: e.target.value });
    validateField(e.target.name, e.target.value);
  };
  const handleChangeDescription = (e: any) => {
    setResultData({ ...resultData, description: e.target.value });
  };

  /**
   * When we select resource
   * Filter resource using id
   * set selected resources in a state
   * set all resource ids in a state
   * @param value
   */
  const onSelectResources = async (dataObj: any) => {
    if (!dataObj?.length) {
      setOpenResourceConfirmation(true);
      setResourceDataObj([]);
      setGlobalVariables(allVariablesList?.rule_Variables || {});
      return;
    }
    setSelectResourceValue((prevValues: any[]) => {
      const validPrevValues = prevValues.filter((item: any) =>
        dataObj.some((dataItem: any) => dataItem === item),
      );
      const newlySelected = dataObj.filter(
        (item: any) => !validPrevValues.includes(item),
      );
      const newState = [...validPrevValues, ...newlySelected];
      processResources(newState);
      return newState;
    });
    const getParentId = dataObj.map((item: any) => {
      return { parent_id: item.id };
    });
    const validIds = dataObj.map((item: any) => item.id);
    const filteredMergeResource = secondaryMergeResource.filter((item: any) =>
      validIds.includes(item.parent_id),
    );

    setSecondaryMergeResource((prev: any) => {
      const existingIds = new Set(prev.map((item: any) => item.parent_id));
      const uniqueParentIds = getParentId.filter(
        (item: any) => !existingIds.has(item.parent_id),
      );
      return [...filteredMergeResource, ...uniqueParentIds];
    });
  };

  const processResources = async (updatedSelectResourceValue: any[]) => {
    try {
      setIsLoaded(true);

      const resourcePromises = updatedSelectResourceValue.map(
        async (item: any) => {
          const rId = item.id;
          const rCode = item.code;
          const resourceColumnDetailId =
            item?.additional_properties?.resource_column_details_id;
          const result: any = await getResourceColumnsDetail({
            resourceColumnDetailId,
          });
          if (result === undefined) {
            showToast(
              "Oops! this resource does not have any associated resource columns",
              "error",
            );
            return Promise.reject("Resource columns not found");
          }
          return { ...item, ...result, rId, rCode };
        },
      );

      const resources = await Promise.all(resourcePromises);

      if (resIds && resIds.length > updatedSelectResourceValue.length) {
        setOpenResourceConfirmation(true);
        setResIds(resources);
        setResourceDataObj(updatedSelectResourceValue);
        return;
      }

      setSelectedResourceIds([]);
      setResIds([]);
      setResourceIds([]);
      setDomainRuleArr([]);

      setIsSelecting(false);
      setSelectedResourceIds(resources);
      setResIds(resources);
      setResourceIds(resources.map(({ rId }: any) => rId));

      if (resources?.length > 0) {
        const uniqueRIds = new Set<number>();
        resources.forEach((item: any) => uniqueRIds.add(item.rId));

        const uniqueObjects = Array.from(uniqueRIds).map((rId) => {
          const item = resources.find((item: any) => item.rId === rId);
          const mergeType = selectedMergeType.find(
            (item: any) => item.rId === rId,
          );
          return {
            rId,
            columns: item?.resource_column_properties?.unique_columns || [],
            merge_type: mergeType?.merge_type,
            resource_code: item.rCode,
          };
        });

        setSelectedResourceColumns(uniqueObjects);

        const updatedFilters =
          customFilters &&
          customFilters?.filter((customFilter: any) =>
            updatedSelectResourceValue.some(
              (item: any) => item?.id === customFilter?.resource_id,
            ),
          );
        const updatedFiltersWithId =
          updatedFilters &&
          updatedFilters.map((filter: any, index: any) => {
            return {
              ...filter,
              id: index,
            };
          });

        const updatedCustomRules = customComparisonRules?.filter(
          (rule: any) => {
            const leftOperandIdPresent = updatedSelectResourceValue.some(
              (item: { id: any }) =>
                item?.id === rule?.resource?.left_operand?.resource_id,
            );
            const rightOperandIdPresent = updatedSelectResourceValue.some(
              (item: { id: any }) =>
                item?.id === rule?.resource?.right_operand?.resource_id,
            );
            return leftOperandIdPresent && rightOperandIdPresent;
          },
        );
        setCustomFilters(updatedFiltersWithId);
        setCustomComparisonRules(updatedCustomRules);

        let resourceInlineVar: any = {};
        let allVariableList: any = {
          ...(allVariablesList?.rule_Variables || {}),
        };
        resources.map((res: any) => {
          resourceInlineVar = {
            ...resourceInlineVar,
            [res.rId]: res?.additional_properties?.inline_variables,
          };
          allVariableList = {
            ...allVariableList,
            ...res?.additional_properties?.inline_variables,
          };
        });
        setAllVariablesList((prev: any) => ({
          ...prev,
          resourceVar: resourceInlineVar,
        }));
        setGlobalVariables(allVariableList);
      }

      if (
        updatedSelectResourceValue &&
        updatedSelectResourceValue?.length !== 0
      ) {
        setErrors((prevError) => ({ ...prevError, resources: "" }));
      }
    } catch (error) {
      console.error("Error selecting resources:", error);
    } finally {
      setIsLoaded(false);
    }
  };

  const buildPayload = () => {
    const transformPossibleMergeColumns = possibleMergeColumns.map(
      (item: any) => {
        const { rId, columns } = item;
        let possible_merge_on_columns: any = {};
        columns.forEach((column: any) => {
          const { key, [key]: values } = column;
          possible_merge_on_columns[key] = values.length > 0 ? values : null;
        });
        const allKeysNull = Object.values(possible_merge_on_columns).every(
          (value) => value === null,
        );
        return {
          rId,
          possible_merge_on_columns: allKeysNull
            ? null
            : possible_merge_on_columns,
        };
      },
    );

    const primaryDataset =
      selectedResourceColumns.length > 0
        ? {
            resource_id: selectedResourceColumns[0].rId,
            resource_code: selectedResourceColumns[0].resource_code,
            merge_on: selectedResourceColumns[0]?.columns,
            merge_type: selectedMergeType[0]?.merge_type ?? "outer",
            possible_merge_on_columns:
              transformPossibleMergeColumns[0]?.possible_merge_on_columns ??
              null,
            secondary_merge_resource: (() => {
              const pr = secondaryMergeResource.find(
                (item: any) =>
                  item.parent_id === selectedResourceColumns[0].rId,
              );
              if (pr && pr?.resource_id) {
                const {
                  resource_column_details_id,
                  isBtnShow,
                  parent_id,
                  resourceColumnDetails,
                  ...rest
                } = pr;
                return rest;
              }
              return null;
            })(),
          }
        : null;

    const secondaryPossibleMergeColumns =
      transformPossibleMergeColumns.slice(1);
    const secondaryDatasets = selectedResourceColumns
      .slice(1)
      .map((item: any) => ({
        resource_id: item.rId,
        resource_code: item.resource_code,
        merge_on: item?.columns,
        merge_type: item?.merge_type ?? "outer",
        secondary_merge_resource: (() => {
          const sr = secondaryMergeResource.find(
            (r: any) => r.parent_id === item.rId,
          );
          if (sr && sr?.resource_id) {
            const {
              resource_column_details_id,
              isBtnShow,
              parent_id,
              resourceColumnDetails,
              ...rest
            } = sr;
            return rest;
          }
          return null;
        })(),
      }));

    const mergedSecondaryDataset = secondaryDatasets.map((dataset: any) => {
      const mc = secondaryPossibleMergeColumns.find(
        (c: any) => c.rId === dataset.resource_id,
      );
      const mt = selectedMergeType.find(
        (m: any) => m.rId === dataset.resource_id,
      );
      return mc
        ? {
            ...dataset,
            possible_merge_on_columns: mc.possible_merge_on_columns,
            merge_type: mt?.merge_type ?? "outer",
          }
        : dataset;
    });

    const filteredDomainComparisonRules = domainComparisonRules.map(
      ({ id, rowId, ...rest }: any) => rest,
    );
    const updatedCustomRules = customComparisonRules.map((ruleItem: any) => {
      const { id, resource, filter_rules, ...rest } = ruleItem;
      return { ...rest, ...resource, filter_rules };
    });
    const resourcesDetailedList = selectedResourceColumns.map((item: any) => ({
      resource_id: item.rId,
      resource_code: item.resource_code,
    }));

    return {
      rule: {
        name: resultData.name,
        description: resultData.description,
        code: resultData.code,
        domain_id: resultData.domain_id,
        domain_name: resultData.domain_name,
        domain_code: resultData.domain_code,
        current_url: `${window.location.origin}/rules/${currentDomainId}/view/`,
        rule_schema: {
          merge_rule: primaryDataset
            ? {
                primary_dataset: primaryDataset,
                secondary_datasets: mergedSecondaryDataset,
              }
            : null,
          filter_rules: customFilters,
          domain_comparison_rules: filteredDomainComparisonRules,
          custom_comparison_rules: updatedCustomRules,
          resources: resourceIds,
          inline_variables: globalVariables,
          adhoc_queries: adhocQueryData,
          resources_detailed_list: resourcesDetailedList,
        },
      },
      rule_tags: resultData.rule_tags || [],
    };
  };

  const handleValidatePayload = async (payload: any) => {
    setIsValidating(true);
    try {
      const validateReqBody = {
        target_endpoint: "/rules",
        method: "POST",
        body: payload,
        query_params: {},
        url_params: {},
      };
      const response = await validateConfigRequest(validateReqBody);
      setValidationResponse(response);
      if (response?.is_valid) {
        showToast("Payload is valid!", "success");
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || "Validation failed", "error");
    } finally {
      setIsValidating(false);
    }
  };

  const handleCopyPayload = async () => {
    const payload = buildPayload();
    await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    showToast("Payload copied to clipboard!", "success");
  };

  /**
   * When we click save rule
   * set unique doamin
   * create primary & secondary dataset
   * create merge rule
   * set final result state update
   */
  const handleSubmit = async () => {
    if (!canSaveScoringConfig) {
      showToast(
        "Please fix the scoring configuration errors before saving.",
        "warning",
      );
      return;
    }
    try {
      // await addEditRuleSchema.validate(resultData, { abortEarly: false });
      const resultDataWithResources = {
        ...resultData,
        secondaryResource: secondaryMergeResource
          .map((item: any, idx: any) => {
            if (item.hasOwnProperty("resource_id")) {
              return {
                resource_id: item?.resource_id ?? null,
                resource_columns: item?.merge_on || [],
              };
            } else {
              return {
                resource_id: "blank",
                resource_columns: ["blank"],
              };
            }
          })
          .filter((item: any) => item !== null),
      };

      if (selectedResourceIds.length === 0) {
        // toast.error("Please select resource");
        setErrors((prevError) => ({
          ...prevError,
          resources: "Please select resource",
        }));
        await addEditRuleSchema.validate(resultDataWithResources, {
          abortEarly: false,
        });
        return;
      } else {
        await addEditRuleSchema.validate(resultDataWithResources, {
          abortEarly: false,
        });
      }
      if (customFilters.length > 0) {
        const isValidCustomFilters = customFilters.every(
          (filter: { resource_id: undefined; sql_query: undefined }) => {
            return filter.resource_id !== "" && filter.sql_query !== "";
          },
        );

        if (!isValidCustomFilters) {
          showToast(
            `Please select "Resource" and enter "Query" in Resource Filter`,
            "warning",
          );
          return;
        }
      }
      if (selectedResourceColumns.length === 0) {
        showToast("Please select resource columns", "error");
        return;
      }
      const isColumnLengthNull = selectedResourceColumns.every(
        (item: any) => item.columns.length === 0,
      );
      if (isColumnLengthNull) {
        showToast("Please select column/columns for all resources", "error");
        return;
      }
      const isSecondaryMergeResourceRequired = secondaryMergeResource.some(
        (item: any) => {
          return (
            item?.isBtnShow &&
            (item.resource_id === undefined || item.resource_id === null)
          );
        },
      );
      if (isSecondaryMergeResourceRequired) {
        showToast(
          "Please select secondary merge resource or remove the field",
          "error",
        );
        return;
      }
      for (const secondary of secondaryMergeResource) {
        const selectedResource = selectedResourceColumns.find(
          (resource: any) => resource.rId === secondary.parent_id,
        );

        if (selectedResource && secondary?.resource_id) {
          const columnsLength = selectedResource.columns.length;
          const mergeOnLength = secondary?.merge_on
            ? secondary.merge_on.length
            : 0;

          const resourceName = resourcesData?.find((item) => {
            return item.id === selectedResource.rId;
          })?.resource_name;

          const secondaryResourceName = resourcesData?.find((item) => {
            return item.id === secondary.resource_id;
          })?.resource_name;

          if (mergeOnLength === 0) {
            showToast(
              "Please select resource column for secondary merge resource",
              "error",
            );
            return;
          }

          if (columnsLength !== mergeOnLength) {
            showToast(
              `The number of merge columns should be similar for the Resource ${resourceName} and the respective  Secondary merge Resource ${secondaryResourceName}`,
              "error",
            );
            return;
          }
        }
      }

      const isSameLength = selectedResourceColumns.every(
        (item: any) =>
          item.columns.length === selectedResourceColumns[0].columns.length,
      );
      if (!isSameLength) {
        showToast(
          "Please select same number of columns for all resources",
          "error",
        );
        return;
      }
      const transformPossibleMergeColumns = possibleMergeColumns.map(
        (item: any) => {
          const { rId, columns } = item;
          let possible_merge_on_columns: any = {};
          columns.forEach((column: any) => {
            const { key, [key]: values } = column;
            possible_merge_on_columns[key] = values.length > 0 ? values : null;
          });
          // Check if all child keys are null
          const allKeysNull = Object.values(possible_merge_on_columns).every(
            (value) => value === null,
          );
          return {
            rId,
            possible_merge_on_columns: allKeysNull
              ? null
              : possible_merge_on_columns,
          };
        },
      );

      const primaryDataset = {
        resource_id: selectedResourceColumns[0].rId,
        resource_code: selectedResourceColumns[0].resource_code,
        merge_on: selectedResourceColumns[0]?.columns,
        merge_type: selectedMergeType[0]?.merge_type ?? "outer",
        possible_merge_on_columns:
          transformPossibleMergeColumns[0].possible_merge_on_columns,
        secondary_merge_resource: (() => {
          const primaryResource = secondaryMergeResource.find(
            (item: any) => item.parent_id === selectedResourceColumns[0].rId,
          );
          if (primaryResource && primaryResource?.resource_id) {
            const {
              resource_column_details_id,
              isBtnShow,
              parent_id,
              resourceColumnDetails,
              ...rest
            } = primaryResource;
            return rest;
          }
          return null;
        })(),
      };

      const secondaryPossibleMergeColumns =
        transformPossibleMergeColumns.slice(1);
      const secondaryDatasets = selectedResourceColumns
        .slice(1)
        .map((item: any) => {
          return {
            resource_id: item.rId,
            resource_code: item.resource_code,
            merge_on: item?.columns,
            merge_type: item?.merge_type ?? "outer",
            secondary_merge_resource: (() => {
              const secondaryResource = secondaryMergeResource.find(
                (resItem: any) => resItem.parent_id === item.rId,
              );
              if (secondaryResource && secondaryResource?.resource_id) {
                const {
                  resource_column_details_id,
                  isBtnShow,
                  parent_id,
                  resourceColumnDetails,
                  ...rest
                } = secondaryResource;
                return rest;
              }
              return null;
            })(),
          };
        });
      const mergedSecondaryDataset = secondaryDatasets.map((dataset: any) => {
        const correspondingMergeColumns = secondaryPossibleMergeColumns.find(
          (mergeColumns: any) => mergeColumns.rId === dataset.resource_id,
        );
        const correspondingMergeType = selectedMergeType.find(
          (mergeType: any) => mergeType.rId === dataset.resource_id,
        );
        if (correspondingMergeColumns) {
          return {
            ...dataset,
            possible_merge_on_columns:
              correspondingMergeColumns.possible_merge_on_columns,
            merge_type: correspondingMergeType?.merge_type ?? "outer",
          };
        }
        return dataset;
      });
      const mergeRule = {
        primary_dataset: primaryDataset,
        secondary_datasets: mergedSecondaryDataset,
      };
      const resourcesDetailedList = selectedResourceColumns.map(
        (item: any) => ({
          resource_id: item.rId,
          resource_code: item.resource_code,
        }),
      );

      // customFilters.map((filterItem: any) => delete filterItem.id);
      let isFilterRulesValid = true;
      const updatedCustomRules = customComparisonRules.map((ruleItem: any) => {
        const { id, resource, filter_rules, ...rest } = ruleItem;
        // filter_rules?.length > 0 &&
        //   filter_rules.map((item: any) => delete item.id);

        if (filter_rules?.length > 0) {
          filter_rules.forEach((item: any) => {
            if (!item.name || !item.sql_query) {
              isFilterRulesValid = false;
              return;
            }

            delete item.id;
          });
        }
        return {
          ...rest,
          ...resource,
          filter_rules,
        };
      });

      if (!isFilterRulesValid) {
        showToast(
          "Resource and SQL Query cannot be empty in custom comparison's filter rules!",
          "warning",
        );
        return;
      }
      if (getRulesWithMissingColumns(customComparisonRules).length > 0) {
        showToast(MISSING_REQUIRED_COLUMNS_MESSAGE, "warning");
        return;
      }
      if (adhocQueryData.length > 0) {
        const isValidFilterRules = adhocQueryData.every(
          (adhoc: { name: undefined; sql_query: undefined }) => {
            return adhoc.name !== "" && adhoc.sql_query !== "";
          },
        );
        if (!isValidFilterRules) {
          showToast(
            "Name and SQL Query cannot be empty in Adhoc Queries!",
            "warning",
          );
          return;
        }
      }
      setResultData(() => ({
        ...resultData,
        rule_schema: {
          merge_rule: mergeRule,
          filter_rules: customFilters,
          domain_comparison_rules: domainComparisonRules,
          custom_comparison_rules: updatedCustomRules,
          resources: resourceIds,
          inline_variables: globalVariables,
          adhoc_queries: adhocQueryData,
          resources_detailed_list: resourcesDetailedList,
        },
      }));
      const filteredDomainComparisonRules = domainComparisonRules.map(
        ({ id, rowId, ...rest }) => rest,
      );
      // Updated payload structure based on new API requirements
      setFinalRule({
        rule: {
          name: resultData.name,
          description: resultData.description,
          code: resultData.code,
          domain_id: resultData.domain_id,
          domain_name: resultData.domain_name,
          domain_code: resultData.domain_code,
          current_url: `${window.location.origin}/rules/${currentDomainId}/view/`,
          rule_schema: {
            merge_rule: mergeRule,
            filter_rules: customFilters,
            domain_comparison_rules: filteredDomainComparisonRules,
            custom_comparison_rules: updatedCustomRules,
            resources: resourceIds,
            inline_variables: globalVariables,
            adhoc_queries: adhocQueryData,
            resources_detailed_list: resourcesDetailedList,
          },
        },
        rule_tags: resultData.rule_tags || [],
      });
    } catch (validationErrors: any) {
      const newErrors: { [key: string]: string } = {};
      if (validationErrors && validationErrors.inner) {
        validationErrors.inner.forEach(
          (error: { path: string | number; message: string }) => {
            newErrors[error.path] = error.message;
          },
        );
      }
      setErrors((prevErrors) => ({
        ...prevErrors,
        ...newErrors,
      }));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (selectedResourceIds.length > 0) {
        const allColumnNamesSet = new Set();

        // Assuming getColumns and tableRowsData are asynchronous functions
        await getColumns();
        await tableRowsData();

        for (const resource of selectedResourceIds) {
          if (resource.resource_column_properties) {
            for (const column of resource.resource_column_properties
              .resource_columns) {
              if (column.column_name) {
                allColumnNamesSet.add(column.column_name);
              }
            }
          }
        }
        const uniqueColumnNames: any = Array.from(allColumnNamesSet); // Convert Set back to an array
      }
    };

    fetchData();
  }, [selectedResourceIds, selectedResourceColumns]);

  const createScoringConfigSilently = (ruleId: number) => {
    if (!scoringConfigValues || !ruleId) return;
    const payload = {
      rule_id: ruleId,
      blend_ratio: scoringConfigValues.blend_ratio ?? 1.0,
      category_weight_missing: scoringConfigValues.category_weight_missing,
      category_weight_mismatch: scoringConfigValues.category_weight_mismatch,
      category_weight_validation:
        scoringConfigValues.category_weight_validation,
      missing_factor: scoringConfigValues.missing_factor,
      mismatch_factor_low: scoringConfigValues.mismatch_factor_low,
      mismatch_factor_medium: scoringConfigValues.mismatch_factor_medium,
      mismatch_factor_high: scoringConfigValues.mismatch_factor_high,
      custom_factor_sql: scoringConfigValues.custom_factor_sql || null,
      custom_factor_target: scoringConfigValues.custom_factor_target || null,
      severity_mappings: scoringConfigValues.severity_mappings || {},
    };
    createComparisonScoringConfig(payload).catch((error) => {
      console.error(
        "Failed to create comparison scoring config for rule:",
        ruleId,
        error,
      );
      showToast(
        "Rule was created but the scoring config could not be saved. You can configure it later from the Rule edit screen under the Scoring Config tab.",
        "warning",
      );
    });
  };

  /**
   * Finally submit a rule & hit api for rule creation
   */
  useEffect(() => {
    const createNewRule = () => {
      createRule(finalRule)
        .then((res) => {
          setIsLoading(false);
          if (res?.id) {
            createScoringConfigSilently(res.id);
          }
          showToast("Rule Create Successfully", "success");
          navigate(`/rules/${currentDomainId}/view/${res?.id}`);
        })
        .catch((err) => {
          setIsLoading(false);
          console.log("err", err);
        });
    };

    if (finalRule && Object.keys(finalRule).length > 0) {
      createNewRule();
    }
  }, [finalRule]);

  /**
   * Set table coloums for comparison rules with unique keys
   */
  const getColumns = () => {
    const columns: GridColDef[] = [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: (params: any) => (
          <IconButton
            className="rotate-arrow"
            size="small"
            tabIndex={-1}
            disabled={
              !["percentage", "absolute"].includes(params?.row?.tolerance_type)
            }
            onClick={() => {
              // toggle in array
              if (expandedRow.includes(params.row.srNo)) {
                setExpandedRow((prev: any) =>
                  prev.filter((item: any) => item !== params.row.srNo),
                );
              } else {
                setExpandedRow((prev: any) => [...prev, params.row.srNo]);
              }
            }}
          >
            {params?.row?.tolerance_value != null ? (
              <ExpandMoreIcon fontSize="inherit" />
            ) : null}
          </IconButton>
        ),
      },
      {
        field: "srNo",
        headerName: "Sr No",
        width: 100,
        maxWidth: 100,
      },
      {
        field: "domain_name",
        headerName: "Domain Name",
        width: 120,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
    ];

    selectedResourceIds.forEach((resourceItem: any) => {
      columns.push({
        headerName: resourceItem.resource_name,
        field: resourceItem.resource_name,
        width: 120,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      });
    });

    columns.push(
      {
        headerName: "Comparison Type",
        field: "comparison_type",
        maxWidth: 120,
        flex: 1,
        renderCell: (params: any) => {
          const handleOperatorChange = (value: any, id: any) => {
            setTableRows((prev) => {
              return prev.map((item) => {
                if (item.srNo === id) {
                  item.comparison_type = value;
                }
                return item;
              });
            });
            setDomainComparisonRules((prev) => {
              return prev.map((item) => {
                if (item.rowId === id) {
                  item.comparison_type = value;
                }
                return item;
              });
            });
          };
          return (
            <>
              <select
                value={params.row?.comparison_type}
                onChange={(e: any) => {
                  handleOperatorChange(e.target.value, params.row.srNo);
                }}
                className="form-control-1 input-sm"
              >
                <option value="equals">==</option>
                <option value="not equals">!=</option>
              </select>
            </>
          );
        },
      },
      {
        headerName: "Actions",
        field: "action",
        maxWidth: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params: any) => {
          const handleToleranceValue = () => {
            setOpenToleranceDialog(true);
            setSelectedRowId(params.row.srNo);
            tableRows.map((row: any) => {
              if (row.srNo === params.row.srNo) {
                setToleranceData({
                  tolerance_type: row?.tolerance_type || "percentage",
                  tolerance_value: row?.tolerance_value || null,
                  fallback_value: row?.fallback_value || null,
                });
              }
            });
            setToleranceData({
              tolerance_type: "percentage",
              tolerance_value: null,
              fallback_value: null,
            });
          };
          return (
            <>
              <Tooltip title="Add Tolerance" placement="top" arrow>
                <Button
                  color="secondary"
                  onClick={handleToleranceValue}
                  className="datagrid-action-btn min-width-40"
                >
                  <IconAddRowBase />
                </Button>
              </Tooltip>
              <Tooltip title="Remove" placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  sx={{ color: "grey" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setTableRows((prev: any) =>
                      prev
                        .filter((prevItem: any) => {
                          return prevItem.srNo !== params.row.srNo;
                        })
                        .map((prevItem: any, index: number) => {
                          return {
                            ...prevItem,
                            srNo: index + 1,
                          };
                        }),
                    );
                    setDomainComparisonRules((prevRules) => {
                      const updatedRules = prevRules.filter(
                        (rule) => rule.rowId !== params.row.srNo,
                      );
                      return updatedRules.map((rule, index) => ({
                        ...rule,
                        rowId: index + 1,
                      }));
                    });
                  }}
                >
                  <IconRemoveBase />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    );

    setColumns(columns);
  };

  /**
   * Set table rows data for unique keys
   * Get domain maping key from selected resource ids
   */
  const tableRowsData = () => {
    if (selectedDomainData?.domain_properties?.columns?.length > 0) {
      const tableRowsData: any = [];
      const resourcesNames: any = [];

      const uniqueColumnsSet = new Set();

      selectedResourceColumns.forEach((res: any) => {
        res.columns.forEach((col: any) => {
          uniqueColumnsSet.add(col);
        });
      });

      // Convert the set back to an array
      const uniqueColumns = Array.from(uniqueColumnsSet);

      selectedDomainData?.domain_properties?.columns.forEach(
        (item: any, idx: any) => {
          const tableRow: any = {
            id: idx + 1,
            domain_name: item?.name,
          };
          selectedResourceIds.forEach((resourceItem: any) => {
            const rId = resourceItem?.rId;
            setSelectedResourceColumns((prev: any) => {
              const isExist = prev.find((item: any) => item.rId === rId);
              if (isExist) {
                return prev;
              }
              return [
                ...prev,
                {
                  rId,
                  columns:
                    resourceItem?.resource_column_properties?.unique_columns,
                },
              ];
            });
            resourcesNames.push(resourceItem.resource_name);
            const resourceColumnItem =
              resourceItem.resource_column_properties?.resource_columns.find(
                (resourceColumnItem: any) =>
                  resourceColumnItem.domain_column === item?.name &&
                  resourceColumnItem?.is_active,
              );
            tableRow[resourceItem.resource_name] =
              resourceColumnItem?.column_name;
            tableRow["column_name"] = resourceColumnItem?.column_name;
          });
          tableRowsData.push(tableRow);
        },
      );

      const result = tableRowsData.filter((tableRowItem: any) => {
        const objectValues = Object.values(tableRowItem);
        return resourcesNames.every(
          (resourceName: any) =>
            tableRowItem[resourceName] !== undefined &&
            !objectValues.some(
              (value) =>
                typeof value === "string" && uniqueColumns.includes(value),
            ),
        );
      });

      const domainRules = result.map((item: any, idx: any) => {
        return {
          rowId: idx + 1,
          id: item?.id,
          name: item?.domain_name,
          column_name: item?.domain_name,
          comparison_type: "equals",
        };
      });

      setDomainComparisonRules(domainRules);

      const resultWithSrNo = result.map((item: any, idx: any) => ({
        ...item,
        srNo: idx + 1,
      }));
      setTableRows(resultWithSrNo);
      setDomainRuleArr(
        result.map((item: any, idx: any) => {
          return {
            ...item,
            id: idx,
          };
        }),
      );
    }
  };

  const handleChangeTab = (event: any, newValue: any) => {
    setActiveTab(newValue);
  };
  const handleChangeAvailableColumns = (value: any, data: any) => {
    if (data?.length > 0) {
      const sortedData = data
        .slice()
        .sort((a: { id: number }, b: { id: number }) => a.id - b.id);
      const domainRules = sortedData.map((item: any, idx: any) => {
        const comparisonData = domainComparisonRules.find(
          (comparisonItem) => comparisonItem.name === item.domain_name,
        );
        if (comparisonData) {
          return {
            rowId: idx + 1,
            name: item.domain_name,
            column_name: item.domain_name,
            comparison_type: comparisonData.comparison_type || "equals",
            tolerance_type: comparisonData.tolerance_type,
            tolerance_value: comparisonData.tolerance_value,
            fallback_value: comparisonData.fallback_value,
          };
        } else {
          return {
            rowId: idx + 1,
            name: item.domain_name,
            column_name: item.domain_name,
            comparison_type: "equals",
            tolerance_type: null,
            tolerance_value: null,
            fallback_value: null,
          };
        }
      });
      setTableRows(
        sortedData.map((item: any, idx: any) => {
          const comparisonData = domainComparisonRules.find(
            (comparisonItem) => comparisonItem.name === item.domain_name,
          );
          if (comparisonData) {
            return {
              ...item,
              srNo: idx + 1,
              tolerance_type: comparisonData.tolerance_type,
              tolerance_value: comparisonData.tolerance_value,
              fallback_value: comparisonData.fallback_value,
              comparison_type: comparisonData.comparison_type || "equals",
            };
          } else {
            return {
              ...item,
              srNo: idx + 1,
              tolerance_type: null,
              tolerance_value: null,
              fallback_value: null,
              comparison_type: "equals",
            };
          }
        }),
      );
      setDomainComparisonRules(domainRules);
    } else {
      setDomainComparisonRules([]);
      setTableRows([]);
    }
  };
  const handleEditToleranceValue = (rowData: any) => {
    setOpenToleranceDialog(true);
    setSelectedRowId(rowData?.srNo);
    setToleranceData({
      tolerance_type: rowData?.tolerance_type,
      tolerance_value: rowData?.tolerance_value,
      fallback_value: rowData?.fallback_value,
    });
  };
  const handleDeleteToleranceValue = (rowData: any) => {
    const updatedDomainComparision = domainComparisonRules.map((rule) => {
      if (rule.rowId === rowData?.srNo) {
        // Remove tolerance_type and tolerance_value for the matching rowId
        return {
          ...rule,
          tolerance_type: null,
          tolerance_value: null,
          fallback_value: null,
        };
      }
      return rule;
    });

    setDomainComparisonRules(updatedDomainComparision);
    const updatedTableRows = tableRows.map((row) => {
      if (row.srNo === rowData?.srNo) {
        // Remove tolerance_type and tolerance_value for the matching rowId
        return {
          ...row,
          tolerance_type: null,
          tolerance_value: null,
          fallback_value: null,
        };
      }
      return row;
    });
    setTableRows(updatedTableRows);
  };
  const tabContent: any = {
    domainComparisonRule: (
      <Box gap="3rem">
        <Box className="text-box-card compact-text-box-card no-radius mb-0 bdr-bottom-0">
          <Grid>
            <Autocomplete
              id={"auto-complete-available-columns"}
              PopperComponent={(props) => {
                return (
                  <Popper
                    {...props}
                    anchorEl={document.getElementById(
                      "auto-complete-available-columns",
                    )}
                  />
                );
              }}
              fullWidth={true}
              className="form-control-autocomplete"
              disabled={Boolean(isSelecting)}
              options={domainRuleArr}
              getOptionLabel={(option: any) => option.domain_name}
              disableCloseOnSelect
              multiple
              onChange={(key: any, value: any) =>
                handleChangeAvailableColumns(key, value)
              }
              renderInput={(params) => (
                <>
                  <div className="autocomplete-chips-direction">
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Available Columns"
                      placeholder="Select..."
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </div>
                </>
              )}
              renderOption={(props, option, { selected }) => (
                <MenuItem
                  {...props}
                  key={option.id}
                  value={option.domain_name}
                  sx={{ justifyContent: "space-between" }}
                >
                  {option.domain_name}
                  {selected ? <CheckCircleRounded color="info" /> : null}
                </MenuItem>
              )}
              value={
                domainRuleArr.filter((item: any) =>
                  tableRows.find(
                    (row: any) => row?.domain_name === item?.domain_name,
                  ),
                ) || null
              }
            />
          </Grid>
        </Box>
        <RulesDataTable
          dataColumns={columns}
          dataRows={tableRows}
          checkboxSelection={true}
          dataListTitle="Comparison Rule List"
          className="dataTable no-radius"
          handleEditToleranceValue={handleEditToleranceValue}
          handleDeleteToleranceValue={handleDeleteToleranceValue}
        />
      </Box>
    ),
    customComparisonRule: (
      <CustomComparisionTab
        selectedResourceIds={selectedResourceIds}
        resourcesData={multipleResourcesData}
      />
    ),
    resourceFilter: <ResourceFilter isLoading={isLoading} />,
    adhocQueries: (
      <AdhocQueries
        adhocQueryData={adhocQueryData}
        setAdhocQueryData={setAdhocQueryData}
        isViewOnly={false}
        errors={errors}
        setErrors={setErrors}
        sampleQuery={sampleAdhocQuery}
      />
    ),
    scoringConfig: (
      <div className="accordion-panel text-box-card compact-text-box-card no-radius bottom-radius bg-white mb-0">
        <ComparisonScoringConfigForm
          ruleId={0}
          existingConfig={null}
          onSaveSuccess={() => {}}
          hideCreateButton={true}
          onValuesChange={(values) => setScoringConfigValues(values)}
          onValidityChange={setIsScoringConfigValid}
        />
      </div>
    ),
  };

  const onSelectResouceColumns = (rId: any, dataObj: any) => {
    const isExist = selectedResourceColumns.find(
      (item: any) => item.rId === rId,
    );
    const updatedColumns = isExist
      ? selectedResourceColumns.map((item: any) => {
          if (item.rId === rId) {
            return {
              ...item,
              columns: dataObj,
            };
          }
          return item;
        })
      : [
          ...selectedResourceColumns,
          {
            rId,
            columns: dataObj,
          },
        ];
    // Find the selected resource from selectedResourceIds
    const selectedResource = selectedResourceIds.find(
      (resource: any) => resource.rId === rId,
    );

    // If the selected resource is found, get the length of its resource columns
    let resourceColumnLength = 0;
    if (selectedResource) {
      resourceColumnLength =
        selectedResource.resource_column_properties.resource_columns.length;
    }

    if (dataObj && resourceColumnLength === dataObj?.length) {
      showToast(
        "Since all columns are unique, the custom comparison for this resource has been removed.",
        "warning",
      );
    }
    setSelectedResourceColumns(updatedColumns);
  };
  const renderSelectedResourcesPopper = useCallback(
    (props: any, ids: string[]) => {
      return (
        <Popper
          {...props}
          anchorEl={document.getElementById(
            `autocomplete-selected-resources-${ids}`,
          )}
        />
      );
    },
    [],
  );
  const renderMultipleResourcesPopper = useCallback((props: any) => {
    return (
      <Popper
        {...props}
        className="zindex1200"
        anchorEl={document.getElementById("autocomplete-multiple-resources")}
      />
    );
  }, []);

  useEffect(() => {
    const transformedData = selectedResourceColumns.map((item: any) => {
      const transformedColumns = item.columns.map((columnName: any) => ({
        key: columnName,
        [columnName]: [],
      }));

      return {
        rId: item.rId,
        columns: transformedColumns,
      };
    });
    setPossibleMergeColumns(transformedData);
    const filteredSelectedResourceColumn = selectedResourceColumns.map(
      (item: any) => {
        const { columns, ...rest } = item;
        return rest;
      },
    );
    setSelectedMergeType(filteredSelectedResourceColumn);
  }, [selectedResourceColumns]);

  const onClickMergeTag = (
    option: any,
    columnsArr: any,
    resourceName: string,
    resourceId: number,
  ) => {
    let modifiedColumnsArr: string[] = [];

    const matchedTag = tagColResult.find(
      (tag: { id: any; key: any }) =>
        tag.id === resourceId && tag.key === option,
    );

    // Find the columns to exclude based on resourceId
    const excludeKeys = possibleMergeColumns
      .filter((item: any) => item.rId === resourceId)
      .flatMap((item: any) => item.columns.map((col: any) => col.key));
    // Set the selectedRows with the values from the matched tag
    if (matchedTag) {
      modifiedColumnsArr = combineArrays(matchedTag?.values, columnsArr);
    } else {
      modifiedColumnsArr = columnsArr;
    }
    modifiedColumnsArr = modifiedColumnsArr.filter(
      (col) => !excludeKeys.includes(col),
    );
    setShowTagsDialog((prevState: any) => ({
      ...prevState,
      [`${resourceName}_${option.replace(" ", "")}`]:
        !prevState[`${resourceName}_${option.replace(" ", "")}`],
    }));
    setSelectedTags({
      id: resourceId,
      key: option,
      values: modifiedColumnsArr,
    });
  };

  const handleChangeMergeType = (event: any, rId: number) => {
    const newMergeType = event.target.value;
    setSelectedMergeType((prevColumns: any) =>
      prevColumns.map((column: any) => {
        if (column.rId === rId) {
          return { ...column, merge_type: newMergeType };
        }
        return column;
      }),
    );
  };

  const handleCloseToleranceDialog = () => {
    setOpenToleranceDialog(false);
    setToleranceData({});
    setSelectedRowId(null);
  };
  const onSaveToleranceDialog = () => {
    setTableRows((prev) => {
      return prev.map((item) => {
        if (item.srNo === selectedRowId) {
          item.tolerance_type = toleranceData.tolerance_type;
          item.tolerance_value = toleranceData.tolerance_value;
          item.fallback_value = toleranceData.fallback_value;
        }
        return item;
      });
    });
    setDomainComparisonRules((prev) => {
      return prev.map((item) => {
        if (item.rowId === selectedRowId) {
          item.tolerance_type = toleranceData.tolerance_type;
          item.tolerance_value = toleranceData.tolerance_value;
          item.fallback_value =
            toleranceData.tolerance_type === "percentage"
              ? toleranceData?.fallback_value
              : null;
        }
        return item;
      });
    });
    setOpenToleranceDialog(false);
  };
  const validateSecondaryResourceField = async (
    name: any,
    index: number,
    element: string,
    value: any,
  ) => {
    try {
      await addEditRuleSchema.validateAt(name, value);
      setErrors((prevErrors: any) => {
        return {
          ...prevErrors,
          [`secondaryResource[${index}].${element}`]: undefined,
        };
      });
    } catch (validationError: any) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };
  const validateField = async (name: any, value: any) => {
    try {
      // Create a partial form data object with only the field being changed
      const partialFormData = { ...resultData, [name]: value };
      await addEditRuleSchema.validateAt(name, partialFormData);
      // If validation passes, clear any previous errors for this field
      setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
    } catch (validationError: any) {
      // If validation fails, set the error message for this field
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationError.message,
      }));
    }
  };
  const handleCancel = () => {
    setOpenConfirmation(false);
  };
  const handleConfirm = () => {
    setOpenConfirmation(false);
    setMultipleResourcesData([]);
    setSelectedResourceIds([]);
    setResourceIds([]);
    setCurrentDomainId(null);
    setTableRows([]);
    setCustomComparisonRules([]);
    setCustomFilters([]);
    setDomainComparisonRules([]);
    setResultData({
      ...resultData,
      domain_id: null,
      domain_name: "",
    });
  };
  const handleChipClick = (option: any) => {
    const url = `/resource/${option?.domain_id}/view/${option?.rId}`;
    window.open(url, "_blank");
  };

  const handleCancelResourceConfirm = () => {
    setOpenResourceConfirmation(false);
  };
  const handleResourceConfirm = () => {
    if (resourceDataObj.length === 0) {
      setSelectedResourceIds([]);
      setResIds([]);
      setResourceIds([]);
      setCustomComparisonRules([]);
      setCustomFilters([]);
      setSelectedResourceColumns([]);
      setTableRows([]);
      setDomainComparisonRules([]);
      setDomainRuleArr([]);
      setColumns([]);
      setErrors((prevError) => ({
        ...prevError,
        resources: "Please select resource",
      }));
    }
    onSelectResources(resourceDataObj);
    setOpenResourceConfirmation(false);
  };
  return (
    <Box>
      <Loader isLoading={isLoaded} />
      <Box>
        <Box className="text-box-card compact-text-box-card no-radius mb-0 top-radius merge-zindex">
          <Grid
            container
            rowSpacing={1.5}
            columnSpacing={2.5}
            sx={{ marginBottom: 2.5 }}
          >
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Rule Name<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="name"
                // placeholder="Ex: Sample Rule Name"
                onChange={handleChangeRule}
                className={`form-control ${errors?.name ? "has-error" : ""}`}
                error={!!errors?.name}
                helperText={errors?.name || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <AutoCompleteDomainList
                setIsLoading={setIsLoading}
                handelOnChangeDomain={handelChangeDomain}
                currentDomainId={currentDomainId}
                className={`form-control-autocomplete ${
                  errors?.domain_id ? "has-error" : ""
                }`}
                required
                error={errors?.domain_id}
                helperText={errors?.domain_id}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Select Resources <span className="required-asterisk">*</span>
              </label>
              <Autocomplete
                id={"autocomplete-multiple-resources"}
                PopperComponent={renderMultipleResourcesPopper}
                fullWidth={true}
                className={`form-control-autocomplete ${
                  errors?.resources ? "has-error" : ""
                }`}
                disabled={Boolean(isSelecting)}
                multiple
                options={multipleResourcesData || []}
                getOptionLabel={(option) => option.resource_name}
                disableCloseOnSelect
                onChange={(key: any, value: any) => onSelectResources(value)}
                renderInput={(params) => (
                  <>
                    <div className="autocomplete-chips-direction">
                      <TextField
                        {...params}
                        variant="outlined"
                        // label="Multiple Resources"
                        placeholder="Select..."
                        InputLabelProps={{
                          shrink: true,
                        }}
                        error={!!errors?.resources}
                        helperText={errors?.resources || ""}
                      />
                    </div>
                  </>
                )}
                renderTags={(value, getTagProps) => {
                  return (
                    <>
                      <div className="align-from-left">
                        {selectedResourceIds.map(
                          (option: any, index: number) => (
                            <Chip
                              {...getTagProps({ index })}
                              key={index}
                              label={option?.resource_name}
                              onClick={() => handleChipClick(option)}
                            />
                          ),
                        )}
                      </div>
                    </>
                  );
                }}
                renderOption={(props, option, { selected }) => (
                  <MenuItem
                    {...props}
                    key={`${option?.id}-${option?.created_on}`}
                    value={option?.resource_name}
                    sx={{ justifyContent: "space-between" }}
                  >
                    {option?.resource_name}
                    {selected ? <CheckCircleRounded color="info" /> : null}
                  </MenuItem>
                )}
                value={
                  resourceIds
                    .map((id) =>
                      (multipleResourcesData || []).find(
                        (option: any) => option.id === id,
                      ),
                    )
                    .filter((option) => option !== undefined) || null
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
              <label className="label-text">
                Code<span className="required-asterisk">*</span>
              </label>
              <TextField
                type="text"
                name="code"
                onChange={handleChangeRule}
                className={`form-control ${errors?.code ? "has-error" : ""}`}
                error={!!errors?.code}
                helperText={errors?.code || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={8} lg={8} xl={8}>
              <label className="label-text"> Description</label>
              <TextField
                type="text"
                name="description"
                // placeholder="Ex: Sample Description"
                onChange={handleChangeDescription}
                className={`form-control`}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              lg={4}
              xl={4}
              className="position-relative"
            >
              <EditAuthTags
                selectedTagIds={resultData?.rule_tags || []}
                onTagsChange={(selectedIds) => {
                  setResultData((prev: any) => ({
                    ...prev,
                    rule_tags: selectedIds,
                  }));
                  validateField("rule_tags", selectedIds);
                }}
                entityName="Rule"
                showToast={showToast}
                errors={errors?.rule_tags}
                validateField={validateField}
                fieldName="rule_tags"
                label="Tag(s)"
                required={true}
                useActiveTagsOnly={true}
                setIsLoading={setIsLoading}
              />
            </Grid>
          </Grid>
          <Grid container rowSpacing={2.5} columnSpacing={4}>
            {/* add auth tag here */}
            <Grid item xs display={"flex"} justifyContent={"space-between"}>
              <Grid container item>
                {selectedResourceIds.length > 0 && (
                  <Grid item xs={12}>
                    <SelectedResourceList
                      selectedResourceIds={selectedResourceIds}
                      selectedResourceColumns={selectedResourceColumns}
                      tagColResult={tagColResult}
                      showTagsDialog={showTagsDialog}
                      tagId={tagId}
                      selectedTags={selectedTags}
                      possibleMergeColumns={possibleMergeColumns}
                      selectedMergeType={selectedMergeType}
                      mergeType={mergeType}
                      resourcesData={resourcesData}
                      resultData={resultData}
                      secondaryMergeResource={secondaryMergeResource}
                      errors={errors}
                      isSelecting={isSelecting}
                      onSelectResouceColumns={onSelectResouceColumns}
                      setTagId={setTagId}
                      onClickMergeTag={onClickMergeTag}
                      renderSelectedResourcesPopper={
                        renderSelectedResourcesPopper
                      }
                      setShowTagsDialog={setShowTagsDialog}
                      setTagColResult={setTagColResult}
                      setPossibleMergeColumns={setPossibleMergeColumns}
                      handleChangeMergeType={handleChangeMergeType}
                      setSecondaryMergeResource={setSecondaryMergeResource}
                      setIsLoaded={setIsLoaded}
                      validateSecondaryResourceField={
                        validateSecondaryResourceField
                      }
                      setResultData={setResultData}
                    />
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    columnGap: 1.5,
                  }}
                >
                  <ViewJsonSplitButton
                    onViewJson={() => {
                      setValidationResponse(null);
                      setIsPayloadDialogOpen(true);
                    }}
                    onCopyJson={handleCopyPayload}
                  />
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleSubmit}
                    className="btn-orange"
                  >
                    <SaveOutlinedIcon /> &nbsp; Save
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          className="mui-tabs"
          variant="scrollable"
        >
          <Tab label="Domain Comparison Rule" value="domainComparisonRule" />
          <Tab label="Custom Comparison Rule" value="customComparisonRule" />
          <Tab label="Resource Filter" value="resourceFilter" />
          <Tab label="Adhoc Queries" value="adhocQueries" />
          <Tab label="Scoring Config" value="scoringConfig" />
        </Tabs>
        <Box>{tabContent[activeTab]}</Box>
      </Box>
      <ToleranceDialog
        openToleranceDialog={openToleranceDialog}
        handleCloseToleranceDialog={handleCloseToleranceDialog}
        onSaveToleranceDialog={onSaveToleranceDialog}
      />
      <ConfirmationDialog
        title={"Confirm removing domain"}
        dialogContent={
          "Removing domain will remove existing data, still want to continue?"
        }
        handleCancel={handleCancel}
        openConfirmation={openConfirmation}
        handleConfirm={handleConfirm}
      />
      <ConfirmationDialog
        title={"Confirm removing resource"}
        dialogContent={
          "Removing resource will delete existing data, still want to continue?"
        }
        handleCancel={handleCancelResourceConfirm}
        openConfirmation={openResourceConfirmation}
        handleConfirm={handleResourceConfirm}
      />
      <ViewPayloadDialog
        openDialog={isPayloadDialogOpen}
        payload={buildPayload()}
        handleDialogClose={() => {
          setIsPayloadDialogOpen(false);
          setValidationResponse(null);
        }}
        dialogTitle="Rule JSON"
        onValidate={handleValidatePayload}
        isValidating={isValidating}
        validationResponse={validationResponse}
      />
    </Box>
  );
};

export default AddRule;
